import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Purse from "../components/Purse";

const METHOD_LABEL = { mobile_money: "Mobile Money", mpesa: "M-Pesa", bank_deposit: "Bank Deposit", cash_pickup: "Cash Pickup" };
const STEPS = ["Country", "Amount", "Recipient", "Review", "Pay"];

const SendMoney = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [countries, setCountries] = useState([]);
  const [sourceCurrencies, setSourceCurrencies] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [country, setCountry] = useState(null);
  const [sourceCurrency, setSourceCurrency] = useState("USD");
  const [amount, setAmount] = useState("100");
  const [quote, setQuote] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/countries").then(r => setCountries(r.data));
    api.get("/recipients").then(r => setRecipients(r.data));
    api.get("/source-currencies").then(r => setSourceCurrencies(r.data));
  }, []);

  useEffect(() => {
    if (!country || !amount) { setQuote(null); return; }
    const a = parseFloat(amount);
    if (isNaN(a) || a <= 0) { setQuote(null); return; }
    api.post("/quote", { source_currency: sourceCurrency, destination_country: country.code, amount: a })
      .then(r => setQuote(r.data)).catch(() => setQuote(null));
  }, [country, sourceCurrency, amount]);

  const filteredRecipients = useMemo(
    () => recipients.filter(r => !country || r.country_code === country.code),
    [recipients, country]
  );

  const canNext = useMemo(() => {
    if (step === 0) return !!country;
    if (step === 1) return !!quote;
    if (step === 2) return !!recipient;
    return true;
  }, [step, country, quote, recipient]);

  const startPayment = async () => {
    if (!recipient || !quote) return;
    setBusy(true);
    // Briefly play the purse-snap micro-animation before redirecting to Stripe
    await new Promise(r => setTimeout(r, 950));
    try {
      const r = await api.post("/transfers/checkout", {
        recipient_id: recipient.id,
        source_currency: sourceCurrency,
        source_amount: parseFloat(amount),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment");
      setBusy(false);
    }
  };

  if (user?.kyc_status !== "approved") {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16" data-testid="send-kyc-gate">
        <div className="ra-card p-10 text-center border-[#E9CFA1] bg-[#FDF6E7]">
          <AlertTriangle className="mx-auto text-[var(--ra-warning)]" size={36} />
          <h2 className="font-heading text-2xl font-bold mt-3">Verify your identity first</h2>
          <p className="text-[var(--ra-muted)] mt-2">For safety and compliance, we require a quick KYC check before your first transfer.</p>
          <Link to="/kyc"><Button className="btn-primary rounded-md mt-5" data-testid="kyc-gate-cta">Complete verification</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="send-root">
      <div className="mb-8 fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-1.5">Send money</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Step {step + 1} of {STEPS.length}: {STEPS[step]}</h1>
      </div>

      <div className="flex items-center gap-1.5 mb-8">
        {STEPS.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[var(--ra-primary)]" : "bg-[var(--ra-border)]"}`} data-testid={`step-pill-${i}`} />
        ))}
      </div>

      <div className="ra-card p-6 sm:p-8 fade-up fade-up-2">
        {step === 0 && (
          <div data-testid="step-country">
            <h2 className="font-heading text-xl font-semibold mb-1">Where are you sending to?</h2>
            <p className="text-sm text-[var(--ra-muted)] mb-5">Pick a destination country.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {countries.map(c => (
                <button key={c.code} onClick={() => setCountry(c)} data-testid={`country-btn-${c.code}`}
                  className={`p-4 rounded-lg border text-left transition-all ${country?.code === c.code ? "border-[var(--ra-primary)] bg-[var(--ra-primary)]/5" : "border-[var(--ra-border)] hover:border-[var(--ra-primary)]/40"}`}>
                  <div className="text-2xl">{c.flag}</div>
                  <div className="font-semibold mt-2">{c.name}</div>
                  <div className="text-xs text-[var(--ra-muted)]">{c.currency}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-amount">
            <h2 className="font-heading text-xl font-semibold mb-1">How much are you sending?</h2>
            <p className="text-sm text-[var(--ra-muted)] mb-5">Live rate, zero fees.</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <Label className="text-sm text-[var(--ra-muted)]">You pay</Label>
                <Select value={sourceCurrency} onValueChange={setSourceCurrency}>
                  <SelectTrigger className="mt-1.5 h-12 rounded-md" data-testid="amount-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {sourceCurrencies.map(c => (
                      <SelectItem key={c.code} value={c.code} data-testid={`src-cur-${c.code}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm text-[var(--ra-muted)]">Amount</Label>
                <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 h-12 rounded-md text-lg font-heading font-semibold" data-testid="amount-input" />
              </div>
            </div>

            {quote && (
              <div className="mt-6 rounded-lg border border-[var(--ra-border)] divide-y divide-[var(--ra-border)] bg-[var(--ra-bg)]" data-testid="quote-preview">
                <Row label="Recipient gets" value={formatMoney(quote.destination_amount, quote.destination_currency)} accent />
                <Row label="Exchange rate" value={`1 ${quote.source_currency} = ${quote.rate.toLocaleString()} ${quote.destination_currency}`} />
                <Row label="Fee" value="Free" />
                <Row label="Total to pay" value={formatMoney(quote.total_charged, quote.source_currency)} bold />
                <Row label="Estimated arrival" value={quote.estimated_arrival} />
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-recipient">
            <h2 className="font-heading text-xl font-semibold mb-1">Who are you sending to?</h2>
            <p className="text-sm text-[var(--ra-muted)] mb-5">Choose a saved recipient in {country?.name}.</p>
            {filteredRecipients.length === 0 ? (
              <div className="border border-dashed border-[var(--ra-border)] rounded-lg p-8 text-center" data-testid="no-recipients">
                <p className="text-sm text-[var(--ra-muted)]">No recipients in {country?.name} yet.</p>
                <Link to="/recipients"><Button className="btn-primary rounded-md mt-4" data-testid="add-recipient-link">Add a recipient</Button></Link>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredRecipients.map(r => (
                  <button key={r.id} onClick={() => setRecipient(r)} data-testid={`recipient-btn-${r.id}`}
                    className={`w-full p-4 rounded-lg border flex items-center gap-3 text-left transition-all ${recipient?.id === r.id ? "border-[var(--ra-primary)] bg-[var(--ra-primary)]/5" : "border-[var(--ra-border)] hover:border-[var(--ra-primary)]/40"}`}>
                    <div className="w-10 h-10 rounded-full bg-[var(--ra-accent)]/15 text-[var(--ra-accent)] font-bold flex items-center justify-center">{r.full_name[0]?.toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{r.full_name}</div>
                      <div className="text-xs text-[var(--ra-muted)]">{METHOD_LABEL[r.delivery_method]} · {r.account_identifier}</div>
                    </div>
                    {recipient?.id === r.id && <CheckCircle2 className="text-[var(--ra-primary)]" size={20} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && quote && recipient && (
          <div data-testid="step-review">
            <h2 className="font-heading text-xl font-semibold mb-1">Review your transfer</h2>
            <p className="text-sm text-[var(--ra-muted)] mb-5">Everything look right? You&apos;ll pay with card on the next step.</p>
            <div className="rounded-lg border border-[var(--ra-border)] divide-y divide-[var(--ra-border)]">
              <Row label="To" value={`${recipient.full_name} (${country.name} ${country.flag})`} />
              <Row label="Method" value={METHOD_LABEL[recipient.delivery_method]} />
              <Row label="Account" value={recipient.account_identifier} />
              <Row label="You send" value={formatMoney(quote.source_amount, quote.source_currency)} />
              <Row label="Fee" value="Free" />
              <Row label="Recipient gets" value={formatMoney(quote.destination_amount, quote.destination_currency)} accent />
              <Row label="Total to pay" value={formatMoney(quote.total_charged, quote.source_currency)} bold />
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-pay" className="text-center py-6">
            <div className="flex justify-center mb-4">
              <Purse size={80} snap={busy} />
            </div>
            <h2 className="font-heading text-2xl font-semibold mb-2">Ready to pay {quote && formatMoney(quote.total_charged, quote.source_currency)}</h2>
            <p className="text-sm text-[var(--ra-muted)] mb-6">You&apos;ll be redirected to our secure Stripe checkout.</p>
            <Button onClick={startPayment} disabled={busy} className="btn-primary rounded-md h-12 px-8" data-testid="pay-with-stripe-btn">
              {busy ? "Sealing your purse…" : "Pay with card"}
            </Button>
          </div>
        )}

        <div className="flex justify-between mt-8">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep(s => Math.max(0, s - 1))} data-testid="step-back-btn">Back</Button>
          {step < STEPS.length - 1 && (
            <Button disabled={!canNext} onClick={() => setStep(s => s + 1)} className="btn-primary rounded-md" data-testid="step-next-btn">
              Continue <ArrowRight size={16} className="ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, accent, bold }) => (
  <div className="px-4 py-3 flex justify-between items-baseline">
    <span className="text-sm text-[var(--ra-muted)]">{label}</span>
    <span className={`font-heading ${bold ? "text-lg font-bold" : "font-semibold"} ${accent ? "text-[var(--ra-primary)]" : ""}`}>{value}</span>
  </div>
);

export default SendMoney;
